(function () {
    SUGApp.service('microsoftService', ['$q', 'SUGUtilities', '$window', function($q, util, w) {

        var service = {
            msConnected: false,
            msLoading: false
        };
        var setMsConnected = function (connected) {
            service.msConnected = !!connected;
        };
        var setMsLoading = function (processing) {
            service.msLoading = !!processing;
        };
        setMsConnected(false);
        setMsLoading(true);

        /* PKCE helpers and auth-code popup + exchange */
        function base64UrlEncode(buffer) {
            const bytes = new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
            return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        }
        // SHA-256 hash function
        async function sha256Plain(plain) {
            const enc = new TextEncoder();
            const data = enc.encode(plain);
            const hash = await crypto.subtle.digest('SHA-256', data);
            return new Uint8Array(hash);
        }
        // Create PKCE code verifier and challenge
        async function createPkcePair() {
            const rand = new Uint8Array(32);
            window.crypto.getRandomValues(rand);
            const verifier = base64UrlEncode(rand);
            const challenge = base64UrlEncode(await sha256Plain(verifier));
            return { verifier, challenge };
        }

        /**
         * Open an OAuth popup using PKCE and return the authorization code.
         * The redirect page must postMessage { code } back to opener (or we can parse if same-origin).
         */
        service.getAuthCodeViaPopup = async function () {
            const clientId = window.globalcfg.microsoftClientid;
            const redirectUri = window.globalcfg.redirectURI;
            if (!clientId || !redirectUri) throw new Error('MS PKCE config missing');

            const { verifier, challenge } = await createPkcePair();
            // store verifier for server exchange
            sessionStorage.setItem('ms_pkce_verifier', verifier);

            const scope = encodeURIComponent('openid profile email offline_access');
            const authUrl =
                'https://login.microsoftonline.com/common/oauth2/v2.0/authorize' +
                '?client_id=' + encodeURIComponent(clientId) +
                '&response_type=code' +
                '&response_mode=query' +
                '&redirect_uri=' + encodeURIComponent(redirectUri) +
                '&scope=' + scope +
                '&code_challenge=' + encodeURIComponent(challenge) +
                '&code_challenge_method=S256' +
                '&prompt=select_account';

            const width = 600, height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            const popup = window.open(authUrl, 'ms-auth-popup', `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`);
            if (!popup) throw new Error('Popup blocked');

            return new Promise((resolve, reject) => {
                let handled = false;

                function handleMessage(event) {
                    // Only accept messages from same origin as our app (if redirect URI is same origin)
                    // If your redirect URI is on a different origin and you trust it, relax this check accordingly.
                    if (event.origin !== window.location.origin) {
                        return;
                    }
                    const data = event.data || {};
                    if (data.code) {
                        handled = true;
                        window.removeEventListener('message', handleMessage);
                        try { popup.close(); } catch (e) {}
                        resolve(data.code);
                    } else if (data.error) {
                        handled = true;
                        window.removeEventListener('message', handleMessage);
                        try { popup.close(); } catch (e) {}
                        reject(new Error(data.error));
                    }
                }
                window.addEventListener('message', handleMessage);

                // Poll popup for redirect URL same-origin fallback (if redirectUri is same origin)
                const poll = setInterval(() => {
                    try {
                        if (!popup || popup.closed) {
                            clearInterval(poll);
                            window.removeEventListener('message', handleMessage);
                            if (!handled) reject(new Error('Popup closed without returning code'));
                        } else {
                            // If popup navigated back to our origin we can read its location.search
                            try {
                                const href = popup.location && popup.location.href;
                                if (href && href.indexOf(window.location.origin) === 0) {
                                    const params = new URL(href).searchParams;
                                    const code = params.get('code');
                                    const error = params.get('error');
                                    if (code || error) {
                                        handled = true;
                                        clearInterval(poll);
                                        window.removeEventListener('message', handleMessage);
                                        try { popup.close(); } catch (e) {}
                                        if (code) resolve(code);
                                        else reject(new Error(error || 'No code returned'));
                                    }
                                }
                            } catch (e) {
                                // cross-origin while on provider domain - ignore
                            }
                        }
                    } catch (ex) {
                        // ignore
                    }
                }, 500);
            });
        };

        /**
         * Exchange authorization code with your backend.
         * Backend should use the code_verifier (retrieved from sessionStorage client-side or provided from client)
         * to request tokens from Microsoft. Adjust endpoint/path as your backend expects.
         */
        service.msPostLogin = function(code) {
            return new Promise(function (resolve, reject) {
                const codeVerifier = sessionStorage.getItem('ms_pkce_verifier') || '';
                // call backend endpoint - adjust path/params as your API expects

                if (!codeVerifier) {
                    reject(new Error('PKCE code verifier missing'));
                    return;
                }

                const idPayload = `${code}&redirectUri=${window.globalcfg.redirectURI}&code_verifier_pkce=${codeVerifier}`;
                
                util.APISyncCallV3(
                    'post',
                    'auth/identityproviderlogin', // update server endpoint if different (e.g., 'auth/microsoft/code')
                    { identityProviderToken: idPayload, identityProviderType: 'microsoft' },
                    15000
                ).then(function (apiResponse) {
                    const res = util.lowerCaseKeys(apiResponse);
                    if (res?.data?.data?.response?.token) {
                        resolve(res.data.data.response.token);
                    } else {
                        reject(new Error('No token returned from backend'));
                    }
                }).catch(function (err) {
                    reject(err);
                });
            });
        };

        /**
         * High-level helper: do PKCE popup, exchange with backend, return backend token
         */
        service.msSignIn = async function () {
            setMsLoading(true);
            try {
                const code = await service.getAuthCodeViaPopup();
                const backendToken = await service.msPostLogin(code);
                setMsConnected(true);
                return backendToken;
            } finally {
                setMsLoading(false);
                // clear verifier for hygiene
                try { sessionStorage.removeItem('ms_pkce_verifier'); } catch(e) {}
            }
        };

        // return service object
        return service;
    }]);
})();